import type * as React from 'react'
import {
  CalendarClockIcon,
  CheckCircleIcon,
  DraftIcon,
  EmptyState,
  SendIcon,
} from '@buffer-mono/popcorn'

import type { PostTab } from './PostTabs'
import styles from './PostEmptyStateByTab.module.css'
import { useSplitEnabled } from '@buffer-mono/features'
import { TemplateSuggestionsCard } from '~publish/pages/Create/Templates/components/EmptyState/TemplateSuggestionsCard'
import { useIsAdmin } from '~publish/contexts/AccountContext'

export const PostEmptyStateByTab = ({
  tab,
  children,
  hasChannels = true,
}: {
  tab: PostTab
  children?: React.ReactNode
  hasChannels?: boolean
}): JSX.Element => {
  const isAdmin = useIsAdmin()

  const { isEnabled: isTemplateSuggestionsEnabled } = useSplitEnabled(
    'geid-template-library-empty-states',
  )

  // Only show template suggestions if the user has channels connected and the template suggestions feature is enabled
  const showTemplateSuggestions =
    isTemplateSuggestionsEnabled && tab === 'queue' && hasChannels

  const emptyStateValues: Record<
    PostTab,
    {
      icon: React.ReactNode
      variant: 'success' | 'primary'
      heading: string
      description: string
    }
  > = {
    approvals: {
      icon: <CheckCircleIcon />,
      variant: 'success',
      heading: 'Good job. You are all done!',
      description: 'This is where drafts awaiting approval will appear',
    },
    queue: {
      icon: <CalendarClockIcon />,
      variant: 'primary',
      heading: 'No posts scheduled',
      description: 'Schedule some posts and they will appear here',
    },
    sent: {
      icon: <SendIcon />,
      variant: 'primary',
      heading: 'No sent posts',
      description:
        'You have not sent any posts yet, once you do they will appear here',
    },
    drafts: {
      icon: <DraftIcon />,
      variant: 'primary',
      heading: 'No drafts',
      description:
        'You have not saved any drafts yet, once you do they will appear here',
    },
  }

  const emptyStateTextWhenNoChannels = isAdmin
    ? 'Connect a channel to start scheduling posts'
    : "Contact an account administrator to access to your team's channels and start scheduling. In the meantime, save content ideas in our Create space."

  const description = !hasChannels
    ? emptyStateTextWhenNoChannels
    : emptyStateValues[tab].description

  const heading = !hasChannels
    ? 'Welcome to Buffer 👋'
    : emptyStateValues[tab].heading

  return (
    <EmptyState size="large" variant={emptyStateValues[tab].variant}>
      <EmptyState.Icon>{emptyStateValues[tab].icon}</EmptyState.Icon>
      <EmptyState.Heading>{heading}</EmptyState.Heading>
      <EmptyState.Description>{description}</EmptyState.Description>
      <EmptyState.Actions className={styles.actions}>
        {children}
      </EmptyState.Actions>
      {showTemplateSuggestions && (
        <div className={styles.templateSuggestionsWrapper}>
          <TemplateSuggestionsCard cta="publish-allChannels-emptyState-suggestions-1" />
        </div>
      )}
    </EmptyState>
  )
}
